import type { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import type { SwingSide, SwingSideGenerator } from "building-types/doors/doorTypes";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type {
  VerticalEdgePlacementNoExpr,
  LateralEdgePlacement,
  HorizontalEdgePlacementMatchExpr,
  HorizontalEdgePlacementNoExpr,
  VerticalEdgePlacementMatchExpr,
} from "building-types/entities/edgePlacementTypes";
import type { OpeningHandle, RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type {
  RoomLayoutEligibleDocEntity,
  EntityInstanceType,
} from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomKind } from "building-types/rooms/roomTypesEnum";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { DoorWallFramingSettings } from "building-types/settings/doorWallFramingSettings";
import type { DoorWallWrappingSettings } from "building-types/settings/doorWallWrappingSettings";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { PartialHomeSettings } from "building-types/settings/homeSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type {
  DoorRoughOpeningSettings,
  WindowRoughOpeningSettings,
} from "building-types/settings/roughOpeningSettingsTypes";
import type { DoorViewNoteTable } from "building-types/settings/viewNotesSettings";
import type { WindowWallFramingSettings } from "building-types/settings/windowWallFramingSettings";
import type { WindowWallWrappingSettings } from "building-types/settings/windowWallWrappingSettings";
import type { BuiltinWindowId } from "building-types/windows/windowId";
import type { WindowSettingsBase } from "building-types/windows/windowSettingsBaseTypes";
import type { Table } from "common/data/table";
import type { Plane } from "geom/planeTypes";
import type { LineAnchor, PolyLine2 } from "geom/polyLine2Types";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { Awning } from "./awning";
import type { OpeningTagPlacement } from "./openingTagPlacementTypes";
import type { WindowLateralAlignment, WindowVerticalAlignment, WindowVerticalDatum } from "./openingTypesEnum";

export interface OpeningBase extends FormulaDisabledDocEntityBase<"OPENING">, RoomLayoutEligibleDocEntity {
  readonly id: PrimaryEntityId<"OPENING">;
  readonly name: string;
  readonly parentId: DocEntityParentId<"ROOM" | "ROOM_LAYOUT">;
  readonly edgeId: string;
  readonly handle?: OpeningHandle | RoomLayoutInstanceHandle;
  readonly handleAuxiliary?: OpeningHandle; // When the handle is RoomLayoutInstanceHandle, this is the handle of the original entity.
  readonly entityInstanceType?: EntityInstanceType;
}

export type OpeningDescriptor =
  | DoorPlacementDeprecated
  | WindowPlacementDeprecated
  | DoorPlacementMatchExpr
  | WindowPlacementMatchExpr;

export type OpeningNoExprDescriptor =
  | DoorPlacementDeprecated
  | WindowPlacementDeprecated
  | DoorPlacementNoExpr
  | WindowPlacementNoExpr;

export type Opening = OpeningDescriptor | SolidOpeningPlacement;
export type OpeningNoExpr = OpeningNoExprDescriptor | SolidOpeningPlacement;

export type TargetedOpeningSettings = Table<PartialHomeSettings>;

export interface SolidOpeningPlacement extends OpeningBase {
  readonly type: "SOLID";
  readonly wallStripDomain: boolean;
}

export interface PlacementDeprecated extends OpeningBase {
  readonly type: "DOOR" | "WINDOW";
  readonly param: number;
  readonly paramNormalized: boolean;
  readonly paramAnchor: LineAnchor;
  readonly wallStripDomain: boolean;
}

export interface WindowPlacementDeprecated
  extends WindowPlacementBase,
    WindowSettingsBase,
    PlacementDeprecated,
    OpeningTagPlacement {
  readonly type: "WINDOW";
}

export type DoorPlacement = DoorPlacementDeprecated | DoorPlacementMatchExpr;
export type WindowPlacement = WindowPlacementDeprecated | WindowPlacementMatchExpr;

export interface DoorPlacementDeprecated extends DoorPlacementBase, PlacementDeprecated, OpeningTagPlacement {
  readonly type: "DOOR";
  readonly useStripLimits?: boolean;
}

interface DoorPlacementBase extends OpeningTagPlacement {
  readonly doorType: ForeignEntityId<"DOOR_DESCRIPTOR"> | BuiltinDoorId;
  readonly reverse?: boolean;
  readonly swingSide?: SwingSide | SwingSideGenerator;
  readonly ignoreOppositeWallCollision?: boolean;
  readonly exteriorCasing?: Partial<ExteriorDoorCasingSettings>;
  readonly interiorCasing?: Partial<InteriorDoorCasingSettings>;
  readonly awningSettings?: Partial<AwningSettings>;
  readonly viewNotes?: DoorViewNoteTable;
  readonly disableAutoDimensions?: boolean;
  readonly reportedName?: string;
  readonly includeDriveway?: boolean;
  readonly awning?: Awning;
  readonly framing?: Partial<DoorWallFramingSettings>;
  readonly wrapping?: Partial<DoorWallWrappingSettings>;
  /**
   * Inherited if left undefined
   */
  readonly verticalOffset?: number;
  /**
   * Inherited if left undefined
   */
  readonly lateralOffset?: number;
  readonly roughOpeningSettings?: Partial<DoorRoughOpeningSettings>;
}

interface WindowPlacementBase
  extends Omit<VerticalEdgePlacementNoExpr, "verticalOffset">,
    Omit<LateralEdgePlacement, "lateralOffset" | "lateralOffsetNormalized"> {
  readonly windowType: ForeignEntityId<"WINDOW_DESCRIPTOR"> | BuiltinWindowId;
  readonly reportedName?: string;
  readonly awning?: Awning;
  readonly framing?: Partial<WindowWallFramingSettings>;
  readonly wrapping?: Partial<WindowWallWrappingSettings>;
  readonly lateralDatum: WallLateralDatumAnchor;
  readonly lateralAlignment: WindowLateralAlignment;
  /**
   * Inherited if left undefined
   */
  readonly lateralOffset?: number;
  /**
   * Inherited if left undefined
   */
  readonly lateralOffsetNormalized?: boolean;
  /**
   * Inherited if left undefined
   */
  readonly verticalOffset?: number;
  readonly verticalDatum: WindowVerticalDatum;
  readonly verticalAlignment: WindowVerticalAlignment;
  readonly roughOpeningSettings?: Partial<WindowRoughOpeningSettings>;
}

interface PlacementMatchExpr extends OpeningBase, HorizontalEdgePlacementMatchExpr {
  readonly type: "DOOR_V2" | "WINDOW_V2" | "STAIR";
  readonly horizontalAlignment: OpeningHorizontalAlignment;
}

interface PlacementNoExpr extends OpeningBase, HorizontalEdgePlacementNoExpr {
  readonly type: "DOOR_V2" | "WINDOW_V2" | "STAIR";
  readonly horizontalAlignment: OpeningHorizontalAlignment;
}

export interface WindowPlacementMatchExpr
  extends Omit<WindowPlacementBase, "verticalOffset" | "verticalDatum">,
    WindowSettingsBase,
    PlacementMatchExpr,
    OpeningTagPlacement,
    VerticalEdgePlacementMatchExpr {
  readonly type: "WINDOW_V2";
  readonly verticalDatum: WindowVerticalDatum;
}

export interface DoorPlacementMatchExpr
  extends Omit<DoorPlacementBase, "verticalOffset">,
    PlacementMatchExpr,
    OpeningTagPlacement,
    VerticalEdgePlacementMatchExpr {
  readonly type: "DOOR_V2";
}

export interface WindowPlacementNoExpr
  extends WindowPlacementBase,
    WindowSettingsBase,
    PlacementNoExpr,
    OpeningTagPlacement {
  readonly type: "WINDOW_V2";
}

export interface DoorPlacementNoExpr extends DoorPlacementBase, PlacementNoExpr, OpeningTagPlacement {
  readonly type: "DOOR_V2";
}

export interface OpeningSideHolePlacement {
  readonly edge: PolyLine2;
  readonly edgeLength: number;
  readonly edgePlane: Plane;
  readonly openingPlane: Plane;
  readonly room?: Room;
  readonly roomKind?: RoomKind;
}

export enum OpeningHorizontalAlignment {
  RIGHT = "RIGHT",
  CENTER = "CENTER",
  LEFT = "LEFT",
}
